/**
 * Product Hub — media and documents.
 *
 * Two entities the spec (§10, §11) keeps deliberately separate from the product itself: an image
 * is uploaded once and may be attached to a product, to a specific variant, and later to a
 * store's override of that product. Copying the file per attachment would mean three uploads of
 * the same photograph and three different URLs to invalidate.
 *
 * Everything a *client* needs to reject a file before it leaves the browser lives here too. The
 * limits are not duplicated in the frontend: the dropzone and the backend guard read the same
 * arrays, so raising the size cap is one edit.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through the
 * `@malash-office/types/product-hub/media` alias.
 */

import { FEEDBACK_FILE_MAX_BYTES, FEEDBACK_FILE_MIME_TYPES } from './feedback.types';
import { PhOption, phOptions } from './productHub.core';
import { INVOICE_SCAN_MAX_BYTES, INVOICE_SCAN_MIME_TYPES } from './supplierInvoiceScan.types';

/* ------------------------------------------------------------------ */
/* Roles                                                               */
/* ------------------------------------------------------------------ */

/**
 * What an attached image is *for*.
 *
 * Only two, and that is the point: `primary` is the one image the product list, the store feed
 * and the completeness check all ask for, and it is enforced as at most one per product by a
 * partial unique index. Everything else is `gallery` and ordered by `sortOrder`. A third role
 * would be a display concern dressed up as data.
 */
export const PH_MEDIA_ROLES = ['primary', 'gallery'] as const;
export type PhMediaRole = (typeof PH_MEDIA_ROLES)[number];

export const PH_MEDIA_ROLE_LABELS: Record<PhMediaRole, string> = {
  primary: 'תמונה ראשית',
  gallery: 'גלריה'
};

export const PH_MEDIA_ROLE_OPTIONS = phOptions(PH_MEDIA_ROLES, PH_MEDIA_ROLE_LABELS);

/* ------------------------------------------------------------------ */
/* Documents                                                           */
/* ------------------------------------------------------------------ */

/** The document kinds the wizard offers. English codes per ADR-017; the Hebrew is below. */
export const PH_DOCUMENT_TYPES = [
  'usage_instructions',
  'technical_spec',
  'certificate',
  'manufacturer_doc',
  'internal_file',
  'other'
] as const;
export type PhDocumentType = (typeof PH_DOCUMENT_TYPES)[number];

export const PH_DOCUMENT_TYPE_LABELS: Record<PhDocumentType, string> = {
  usage_instructions: 'הוראות שימוש',
  technical_spec: 'מפרט טכני',
  certificate: 'אישור / תעודה',
  manufacturer_doc: 'מסמך יצרן',
  internal_file: 'קובץ פנימי',
  other: 'אחר'
};

export const PH_DOCUMENT_TYPE_OPTIONS = phOptions(PH_DOCUMENT_TYPES, PH_DOCUMENT_TYPE_LABELS);

/**
 * Who may see a document.
 *
 * `store_allowed` is the spec's name (§11); the mockup writes `store`. The spec wins because this
 * value is what a store integration will branch on, and "allowed" says what the flag actually
 * grants — permission, not a destination.
 */
export const PH_DOCUMENT_VISIBILITIES = ['internal', 'store_allowed'] as const;
export type PhDocumentVisibility = (typeof PH_DOCUMENT_VISIBILITIES)[number];

export const PH_DOCUMENT_VISIBILITY_LABELS: Record<PhDocumentVisibility, string> = {
  internal: 'פנימי בלבד',
  store_allowed: 'מותר להצגה בחנות'
};

export const PH_DOCUMENT_VISIBILITY_OPTIONS = phOptions(PH_DOCUMENT_VISIBILITIES, PH_DOCUMENT_VISIBILITY_LABELS);

/* ------------------------------------------------------------------ */
/* Upload limits                                                       */
/* ------------------------------------------------------------------ */

/**
 * The image types accepted.
 *
 * **SVG is excluded on purpose.** An SVG is a script container, and these files are served from
 * the bucket's own origin — an uploaded `<svg><script>` would run there with whatever that origin
 * is trusted for. Nothing in a product catalogue needs vector art badly enough to take that.
 */
export const PH_MEDIA_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'] as const;

/** 10 MB. A product photograph straight off a phone is 3-5 MB; anything past this is a mistake. */
export const PH_MEDIA_MAX_BYTES = 10 * 1024 * 1024;

export const PH_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
  'image/jpeg',
  'image/png'
] as const;

/** 25 MB — a scanned certificate with photographs in it is the realistic worst case. */
export const PH_DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;

/**
 * 500 MB — a file on a פיתוח מוצרים card (2026-10-20, by request): print-ready artwork and press
 * PDFs run far past a scanned certificate. Same types as a document; only the ceiling differs, and
 * the route that takes it streams through disk rather than holding the file in memory.
 */
export const PLANNING_FILE_MAX_BYTES = 500 * 1024 * 1024;

/** Which set of rules applies to an upload. */
export type PhUploadKind = 'media' | 'document' | 'planningFile' | 'feedbackFile' | 'invoiceScan';

const RULES: Record<PhUploadKind, { mimeTypes: readonly string[]; maxBytes: number; noun: string }> = {
  media: { mimeTypes: PH_MEDIA_MIME_TYPES, maxBytes: PH_MEDIA_MAX_BYTES, noun: 'תמונה' },
  document: { mimeTypes: PH_DOCUMENT_MIME_TYPES, maxBytes: PH_DOCUMENT_MAX_BYTES, noun: 'מסמך' },
  planningFile: { mimeTypes: PH_DOCUMENT_MIME_TYPES, maxBytes: PLANNING_FILE_MAX_BYTES, noun: 'קובץ' },
  // פניות ומשוב: screenshots, a PDF or a short screen recording (2026-10-21).
  feedbackFile: { mimeTypes: FEEDBACK_FILE_MIME_TYPES, maxBytes: FEEDBACK_FILE_MAX_BYTES, noun: 'קובץ' },
  // סריקת חשבונית ספק: what the model reads and the invoice's files keep (2026-10-21).
  invoiceScan: { mimeTypes: INVOICE_SCAN_MIME_TYPES, maxBytes: INVOICE_SCAN_MAX_BYTES, noun: 'חשבונית' }
};

/** Megabytes, for a message. Whole numbers only — every limit here is one. */
const asMb = (bytes: number): number => Math.round(bytes / (1024 * 1024));

/** Why a file was refused. The backend maps this to a `PH_` code; the UI just shows the message. */
export type PhUploadRejectionReason = 'type' | 'empty' | 'size';

export interface PhUploadRejection {
  reason: PhUploadRejectionReason;
  /** Hebrew, shown to the user as-is by both the dropzone and the API error body. */
  message: string;
}

/**
 * Why this file cannot be uploaded — or **null when it can**.
 *
 * Returning the problem rather than a boolean means the caller cannot accidentally use the answer
 * without the reason, and `if (rejection)` reads the same in both callers. Pure and
 * dependency-free so the dropzone can call it on a `File` before uploading and the controller can
 * call it on the parsed multipart part after: same verdict, same sentence, one implementation.
 * The browser check is a courtesy to the user; the server check is the one that counts.
 */
export const validateUploadFile = (
  file: { name?: string; size: number; type: string },
  kind: PhUploadKind
): PhUploadRejection | null => {
  const { mimeTypes, maxBytes, noun } = RULES[kind];

  if (!mimeTypes.includes(file.type)) {
    return { reason: 'type', message: `סוג הקובץ אינו נתמך עבור ${noun}` };
  }

  // Zero bytes reaches the bucket happily and then renders as a broken thumbnail forever.
  if (file.size <= 0) {
    return { reason: 'empty', message: 'הקובץ ריק' };
  }

  if (file.size > maxBytes) {
    return { reason: 'size', message: `הקובץ גדול מדי — עד ${asMb(maxBytes)} MB` };
  }

  return null;
};

/* ------------------------------------------------------------------ */
/* Wire shapes                                                         */
/* ------------------------------------------------------------------ */

/** An uploaded file, independent of anything it is attached to. */
export interface PhMediaAsset {
  id: number;
  fileUrl: string;
  thumbnailUrl: string | null;
  fileType: string;
  title: string | null;
  altText: string | null;
  width: number | null;
  height: number | null;
  fileSize: number | null;
  createdAt: string;
}

/** One attachment of an asset to a product or a variant: the asset plus its role and place. */
export interface PhMediaLink {
  id: number;
  mediaId: number;
  role: PhMediaRole;
  sortOrder: number;
  asset: PhMediaAsset;
}

export interface PhDocumentAsset {
  id: number;
  fileUrl: string;
  fileName: string;
  documentType: PhDocumentType;
  title: string;
  description: string | null;
  visibility: PhDocumentVisibility;
  fileSize: number | null;
  createdBy: number | null;
  createdAt: string;
}

export interface PhDocumentLink {
  id: number;
  documentId: number;
  sortOrder: number;
  asset: PhDocumentAsset;
}

/** The label maps, in the shape `Select` and `FilterConfig` already take. */
export const PH_MEDIA_OPTION_SETS: Record<string, PhOption[]> = {
  role: PH_MEDIA_ROLE_OPTIONS,
  documentType: PH_DOCUMENT_TYPE_OPTIONS,
  visibility: PH_DOCUMENT_VISIBILITY_OPTIONS
};
